import type { ReactNode, RefObject } from "react";
import React, { useEffect, useRef, useState } from "react";
import Circle from "ui/components/Circle";
import type { LinkProps } from "ui/components/Link";
import Link from "ui/components/Link";
import cx from "ui/cx";
import useOutsideClick from "ui/hooks/useOutsideClick";
import IconEllipsis from "ui/icons/IconEllipsis";
import IconMiniChevronDown from "ui/icons/IconMiniChevronDown";
import Tooltip from "ui/popovers/Tooltip";
import styles from "./DropdownMenu.module.scss";

interface DropdownMenuProps {
  children: ReactNode;
  onClose: () => void;
  topAligned?: boolean;
  anchorDirection?: "left" | "right";
  opener?: RefObject<HTMLElement | null>;
}

export default function DropdownMenu({
  children,
  onClose,
  topAligned = true,
  anchorDirection = "right",
  opener,
}: DropdownMenuProps) {
  const dropdown = useRef<HTMLDivElement>(null);
  useOutsideClick(opener !== undefined ? [dropdown, opener] : dropdown, onClose);

  return (
    <div ref={dropdown} className={styles.wrapper} data-testid="dropdown-menu">
      <div
        className={cx("dropdownMenu", {
          styles,
          topAligned: topAligned,
          bottomAligned: !topAligned,
          [anchorDirection]: anchorDirection,
        })}
      >
        {children}
      </div>
    </div>
  );
}

interface DropdownMenuButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  open: boolean;
  handleClick: (e: any) => void;
  handleMouseEnter?: () => void;
  children?: ReactNode;
  hoverBlue?: boolean;
  ellipsisSize?: number;
  overGreyBackground?: boolean;
  disabled?: boolean;
}

export function DropdownMenuButton({
  open,
  handleClick,
  handleMouseEnter,
  children,
  hoverBlue = false,
  ellipsisSize = 16,
  overGreyBackground = false,
  disabled = false,
  className,
  ...props
}: DropdownMenuButtonProps) {
  return (
    <button
      data-testid="dropdown-menu-button"
      className={cx(
        styles.dropdownMenuButton,
        {
          styles,
          dropdownMenuButtonActive: open,
          dropdownMenuButtonBlueHover: hoverBlue,
          dropDownMenuButtonOnGreyBgHover: overGreyBackground,
          disabled,
        },
        className,
      )}
      onClick={handleClick}
      onMouseEnter={() => {
        if (!disabled) {
          handleMouseEnter && handleMouseEnter();
        }
      }}
      {...props}
    >
      {children ?? (
        <Circle size={30} className="dropdown-button-circle">
          <IconEllipsis color="stoneDark" size={ellipsisSize} />
        </Circle>
      )}
    </button>
  );
}

export type DropdownMenuItem = LinkProps & { hide?: boolean };

export interface DropdownMenuAndButtonProps extends React.HTMLAttributes<HTMLDivElement> {
  menuItems: DropdownMenuItem[];
  ellipsisSize?: number;
  // serves as an indicator from a parent to close
  shouldClose?: boolean;
  closeOnSelection?: boolean;
  buttonProps?: Partial<Omit<DropdownMenuButtonProps, "open" | "handleClick" | "ellipsisSize">>;
  dropdownMenuProps?: Pick<DropdownMenuProps, "anchorDirection" | "topAligned">;
}

export function DropdownMenuAndButton({
  menuItems,
  ellipsisSize,
  shouldClose,
  closeOnSelection = true,
  className,
  children,
  buttonProps,
  dropdownMenuProps,
  ...props
}: DropdownMenuAndButtonProps) {
  const [openMenu, setOpenMenu] = useState(false);
  const dropdownButton = useRef(null);

  useEffect(() => {
    if (shouldClose) {
      setOpenMenu(false);
    }
  }, [shouldClose]);

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <div style={{ position: "relative" }} ref={dropdownButton} className={className} {...props}>
      <DropdownMenuButton
        {...buttonProps}
        open={openMenu}
        handleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpenMenu(!openMenu);
        }}
        ellipsisSize={ellipsisSize}
      >
        {children}
      </DropdownMenuButton>
      {openMenu && (
        <DropdownMenu onClose={() => setOpenMenu(false)} {...dropdownMenuProps}>
          {menuItems.map(
            ({ hide, ...item }: DropdownMenuItem, i) =>
              !hide && (
                <Link
                  key={typeof item.children === "string" ? item.children + i : i}
                  {...item}
                  onClick={(e) => {
                    e?.stopPropagation();
                    if (closeOnSelection) {
                      setOpenMenu(false);
                    }
                    item.onClick?.(e);
                  }}
                />
              ),
          )}
        </DropdownMenu>
      )}
    </div>
  );
}

interface SimpleDropDownMenuProps {
  menuItems: ReactNode[];
  ellipsisSize?: number;
  disableOpen?: boolean;
}

export function SimpleDropDownMenu({ menuItems, ellipsisSize, disableOpen = false }: SimpleDropDownMenuProps) {
  const [openMenu, setOpenMenu] = useState(false);

  return (
    <div className="dropdownMenuWrapper">
      <DropdownMenuButton
        open={openMenu}
        handleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disableOpen) {
            setOpenMenu(!openMenu);
          }
        }}
        ellipsisSize={ellipsisSize}
      />
      {openMenu && (
        <DropdownMenu onClose={() => setOpenMenu(false)}>
          {menuItems.map((element, index) => (
            <React.Fragment key={index}>{element}</React.Fragment>
          ))}
        </DropdownMenu>
      )}
    </div>
  );
}

interface IconDropdownMenuProps {
  menuItems: ReactNode;
  selectedIcon?: ReactNode;
  disabled?: boolean;
  tooltipText?: string;
  className?: string;
}

export function IconDropdown({
  menuItems,
  selectedIcon,
  disabled = false,
  tooltipText,
  className,
}: IconDropdownMenuProps) {
  const [openMenu, setOpenMenu] = useState(false);
  const tooltipEnabled = disabled && tooltipText;

  const dropdownContent = (
    <div
      className={cx(styles.iconDropDownWrapper, disabled && styles.disabled, className)}
      onClick={!disabled ? () => setOpenMenu((prev) => !prev) : undefined}
    >
      {openMenu && (
        <div className={styles.iconDropDown}>
          <DropdownMenu
            onClose={() =>
              // timeout prevents a race condition with the other onClick
              window.setTimeout(() => setOpenMenu(false), 200)
            }
          >
            {menuItems}
          </DropdownMenu>
        </div>
      )}
      {selectedIcon}
      <IconMiniChevronDown />
    </div>
  );

  if (tooltipEnabled) {
    return (
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger>{dropdownContent}</Tooltip.Trigger>
        <Tooltip.Content side="bottom">{tooltipText}</Tooltip.Content>
      </Tooltip.Root>
    );
  }

  return dropdownContent;
}

interface CustomButtonDropdownProps {
  menuItems: ReactNode;
  customButton: ReactNode;
  anchorDirection?: "left" | "right";
  disabled?: boolean;
  tooltipText?: string;
  className?: string;
}

export function CustomButtonDropdown({
  menuItems,
  customButton,
  anchorDirection = "right",
  disabled = false,
  tooltipText,
  className,
}: CustomButtonDropdownProps) {
  const [openMenu, setOpenMenu] = useState(false);
  const tooltipEnabled = disabled && tooltipText;

  const dropdownContent = (
    <div
      className={cx(styles.iconDropDownWrapper, disabled && styles.disabled, className)}
      onClick={!disabled ? () => setOpenMenu((prev) => !prev) : undefined}
    >
      {openMenu && (
        <div className={styles.iconDropDown}>
          <DropdownMenu
            anchorDirection={anchorDirection}
            onClose={() =>
              // timeout prevents a race condition with the other onClick
              window.setTimeout(() => setOpenMenu(false), 200)
            }
          >
            {menuItems}
          </DropdownMenu>
        </div>
      )}
      {customButton}
    </div>
  );

  if (tooltipEnabled) {
    return (
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger>{dropdownContent}</Tooltip.Trigger>
        <Tooltip.Content side="bottom">{tooltipText}</Tooltip.Content>
      </Tooltip.Root>
    );
  }

  return dropdownContent;
}
